/**
 * Auth API Client (Story 1.6)
 *
 * Client-side authentication utilities:
 * - Token refresh via /api/auth/refresh
 * - Logout via /api/auth/logout
 * - Token extraction from OAuth redirect
 *
 * SECURITY: Refresh token is in HttpOnly cookie (handled by browser).
 * Access token is stored in memory via Zustand (useAuthStore).
 */

import { useAuthStore } from "../stores/authStore";
import { queryClient } from "./queryClient";
import { router } from "../router";
import { apiUrl } from "./apiBase";

/**
 * Refresh result from the API
 */
interface RefreshResponse {
  data: {
    access_token: string;
    expires_in: number;
  };
}

/**
 * Decode JWT claims without verification
 *
 * WARNING: This only decodes - it does NOT verify the signature.
 * Server must verify tokens. This is used for extracting user info client-side.
 *
 * @param token - JWT access token
 * @returns Decoded payload or null if invalid
 */
export function decodeJwtClaims(token: string): {
  sub: string;
  tenant_id: string;
  email: string;
  role: "owner" | "admin" | "viewer";
  plan: "free" | "starter" | "pro" | "agency";
  jti: string;
  iat: number;
  exp: number;
} | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const payload = parts[1];
    if (!payload) return null;
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Refresh the access token using the HttpOnly cookie
 *
 * Calls POST /api/auth/refresh which reads the refresh token from
 * the HttpOnly cookie and returns a new access token.
 *
 * @returns New access token or null on failure
 */
export async function refreshAccessToken(): Promise<string | null> {
  try {
    console.log("[Auth] Attempting token refresh...");
    const response = await fetch(apiUrl("/api/auth/refresh"), {
      method: "POST",
      credentials: "include", // Include HttpOnly cookie
      headers: {
        "Content-Type": "application/json",
      },
    });

    console.log("[Auth] Refresh response status:", response.status);

    if (!response.ok) {
      // Token refresh failed - user needs to re-login
      const errorBody = await response.text();
      console.log("[Auth] Token refresh failed:", response.status, errorBody);
      return null;
    }

    const data: RefreshResponse = await response.json();

    // Update Zustand store with new token
    const { setAccessToken } = useAuthStore.getState();
    setAccessToken(data.data.access_token, data.data.expires_in);

    // Decode claims to update user info
    const claims = decodeJwtClaims(data.data.access_token);
    if (claims) {
      const { setUser } = useAuthStore.getState();
      setUser({
        id: claims.sub,
        email: claims.email,
        tenantId: claims.tenant_id,
        role: claims.role,
        plan: claims.plan,
      });
    }

    return data.data.access_token;
  } catch (error) {
    console.error("[Auth] Token refresh error:", error);
    return null;
  }
}

/**
 * Logout the user
 *
 * Calls POST /api/auth/logout to revoke the refresh token,
 * then clears the local auth state.
 *
 * @param navigate - Optional navigation function to redirect after logout
 * @param redirectTo - Destination path (defaults to "/login")
 */
export async function logout(
  navigate?: (to: string) => void,
  redirectTo: string = "/login"
): Promise<void> {
  try {
    // Call logout endpoint (will clear cookie server-side)
    await fetch(apiUrl("/api/auth/logout"), {
      method: "POST",
      credentials: "include",
    });
  } catch (error) {
    // Log but continue - we still want to clear local state
    console.error("[Auth] Logout API error:", error);
  }

  // Clear local auth state and cached data
  const { clearAuth } = useAuthStore.getState();
  clearAuth();
  queryClient.clear();

  // Synchronously update router context so _auth beforeLoad sees cleared state
  // during the navigation (prevents redirect back to /dashboard)
  router.update({
    context: {
      auth: {
        isAuthenticated: false,
        isLoading: false,
        user: null,
      },
    },
  });

  // Redirect if navigate function provided
  if (navigate) {
    navigate(redirectTo);
  }
}

/**
 * Logout all sessions response from the API
 */
interface LogoutAllResponse {
  data: {
    access_token: string;
    expires_in: number;
    revoked_count: number;
  };
}

/**
 * Logout all sessions except current device
 *
 * Calls POST /api/auth/logout-all to revoke all refresh tokens,
 * then updates the auth store with the new token pair.
 *
 * @returns The number of revoked sessions, or null on failure
 */
export async function logoutAll(): Promise<number | null> {
  try {
    const { accessToken } = useAuthStore.getState();
    if (!accessToken) {
      console.error("[Auth] Logout-all failed: no access token available");
      return null;
    }
    const response = await fetch(apiUrl("/api/auth/logout-all"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error("[Auth] Logout-all failed:", response.status, errorBody);
      return null;
    }

    const data: LogoutAllResponse = await response.json();

    // Update auth store with new access token
    const { setAccessToken, setUser } = useAuthStore.getState();
    setAccessToken(data.data.access_token, data.data.expires_in);

    // Decode claims to update user info
    const claims = decodeJwtClaims(data.data.access_token);
    if (claims) {
      setUser({
        id: claims.sub,
        email: claims.email,
        tenantId: claims.tenant_id,
        role: claims.role,
        plan: claims.plan,
      });
    }

    return data.data.revoked_count;
  } catch (error) {
    console.error("[Auth] Logout-all error:", error);
    return null;
  }
}

/**
 * Extract access token from OAuth redirect URL fragment
 *
 * OAuth callback redirects with token in URL fragment:
 * /dashboard#access_token=xxx&expires_in=900
 *
 * This function extracts and stores the token.
 *
 * @param hash - URL hash (window.location.hash)
 * @returns true if token was extracted and stored
 */
export function extractTokenFromHash(hash: string): boolean {
  if (!hash || hash.length <= 1) {
    return false;
  }

  // Remove leading #
  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get("access_token");
  const expiresIn = params.get("expires_in");

  if (!accessToken || !expiresIn) {
    return false;
  }

  const expiresInSeconds = parseInt(expiresIn, 10);
  if (isNaN(expiresInSeconds)) {
    return false;
  }

  // Store token in Zustand
  const { setAccessToken, setUser } = useAuthStore.getState();
  setAccessToken(accessToken, expiresInSeconds);

  // Decode claims to set user info
  const claims = decodeJwtClaims(accessToken);
  if (claims) {
    setUser({
      id: claims.sub,
      email: claims.email,
      tenantId: claims.tenant_id,
      role: claims.role,
      plan: claims.plan,
    });
  }

  return true;
}

/**
 * Clear token from URL hash (for security)
 *
 * After extracting the token, remove it from the URL to prevent
 * accidental sharing or logging.
 */
export function clearHashFromUrl(): void {
  if (window.location.hash) {
    // Use replaceState to avoid adding to browser history
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
  }
}
